import React, { useEffect, useMemo, useState } from "react";
import { tradeGet, tradeJson } from "../../utils/tradeApi";

const emptyEditor = { _id: "", name: "", email: "", company: "", active: true, allowedProjects: [], subscriptions: [] };

function subscriptionKey(projectId, categoryId) { return `${projectId}:${categoryId}`; }

export default function TradeSubscriberManager({ projects }) {
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState({});
  const [editor, setEditor] = useState(emptyEditor);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const selectedSubscriptions = useMemo(() => new Set(
    (editor.subscriptions || []).map((item) => subscriptionKey(
      item.projectId || item.project_id,
      item.categoryId || item.trade_category_id,
    )),
  ), [editor.subscriptions]);
  const notificationSubscriptions = useMemo(() => new Set(
    (editor.subscriptions || [])
      .filter((item) => (item.receiveNotifications ?? item.receive_notifications ?? true) === true)
      .map((item) => subscriptionKey(
        item.projectId || item.project_id,
        item.categoryId || item.trade_category_id,
      )),
  ), [editor.subscriptions]);

  async function load() {
    setLoading(true); setError("");
    try {
      const [userData, categoryPairs] = await Promise.all([
        tradeGet("/api/trade/admin/users"),
        Promise.all(projects.map(async (project) => [
          project._id,
          await tradeGet(`/api/trade/projects/${project._id}/categories`),
        ])),
      ]);
      setUsers(userData);
      setCategories(Object.fromEntries(categoryPairs));
    } catch (requestError) { setError(requestError.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { if (projects.length) load(); else setLoading(false); }, [projects]); // eslint-disable-line react-hooks/exhaustive-deps

  function edit(user) {
    setEditor({
      ...emptyEditor,
      ...user,
      allowedProjects: user.allowedProjects || [],
      subscriptions: user.subscriptions || [],
      active: user.active !== false,
    });
    setMessage(""); setError("");
  }

  function toggleProject(projectId) {
    const enabled = editor.allowedProjects.includes(projectId);
    setEditor((current) => ({
      ...current,
      allowedProjects: enabled
        ? current.allowedProjects.filter((id) => id !== projectId)
        : [...current.allowedProjects, projectId],
      subscriptions: enabled
        ? current.subscriptions.filter((item) => (item.projectId || item.project_id) !== projectId)
        : current.subscriptions,
    }));
  }

  function toggleAccess(projectId, categoryId) {
    const key = subscriptionKey(projectId, categoryId);
    setEditor((current) => {
      const exists = current.subscriptions.some((item) => subscriptionKey(
        item.projectId || item.project_id, item.categoryId || item.trade_category_id,
      ) === key);
      return {
        ...current,
        allowedProjects: current.allowedProjects.includes(projectId)
          ? current.allowedProjects : [...current.allowedProjects, projectId],
        subscriptions: exists
          ? current.subscriptions.filter((item) => subscriptionKey(
            item.projectId || item.project_id, item.categoryId || item.trade_category_id,
          ) !== key)
          : [...current.subscriptions, { projectId, categoryId, receiveNotifications: true }],
      };
    });
  }

  function toggleNotification(projectId, categoryId) {
    const key = subscriptionKey(projectId, categoryId);
    setEditor((current) => ({
      ...current,
      subscriptions: current.subscriptions.map((item) => {
        const itemKey = subscriptionKey(
          item.projectId || item.project_id,
          item.categoryId || item.trade_category_id,
        );
        if (itemKey !== key) return item;
        const enabled = item.receiveNotifications ?? item.receive_notifications ?? true;
        return { ...item, receiveNotifications: !enabled };
      }),
    }));
  }

  async function save(event) {
    event.preventDefault(); setSaving(true); setError(""); setMessage("");
    const payload = {
      name: editor.name.trim(), email: editor.email.trim(), company: editor.company.trim(),
      active: editor.active, allowedProjects: editor.allowedProjects,
      subscriptions: editor.subscriptions.map((item) => ({
        projectId: item.projectId || item.project_id,
        categoryId: item.categoryId || item.trade_category_id,
        receiveNotifications: item.receiveNotifications ?? item.receive_notifications ?? true,
      })),
    };
    try {
      if (editor._id) await tradeJson(`/api/trade/admin/users/${editor._id}`, "PUT", payload);
      else await tradeJson("/api/trade/admin/users", "POST", payload);
      setMessage(editor._id ? "Subscriber updated." : "Subscriber created with the configured default password.");
      setEditor(emptyEditor);
      await load();
    } catch (requestError) { setError(requestError.message); }
    finally { setSaving(false); }
  }

  async function resetPassword(user) {
    if (!window.confirm(`Reset ${user.email} to the configured default password?`)) return;
    try {
      await tradeJson(`/api/trade/admin/users/${user._id}/reset-password`, "POST");
      setMessage(`Password reset for ${user.email}.`); await load();
    } catch (requestError) { setError(requestError.message); }
  }

  return (
    <div className="trade-admin-grid">
      <section className="trade-card">
        <div className="trade-section-heading"><div><span className="trade-eyebrow trade-green">ACCESS</span><h2>Trade subscribers</h2></div><button className="trade-button secondary" onClick={() => edit(emptyEditor)}>New subscriber</button></div>
        {loading && <div className="trade-loading">Loading subscribers…</div>}
        {error && <div className="trade-alert error">{error}</div>}
        {!loading && users.length === 0 && <div className="trade-empty">No Trade subscribers yet.</div>}
        <div className="trade-user-list">
          {users.map((user) => (
            <article key={user._id} className="trade-user-row">
              <div className="trade-avatar">{(user.name || user.email || "T").slice(0, 1).toUpperCase()}</div>
              <div><strong>{user.name}</strong><span>{user.email}</span><small>{user.company || "No company"} · {user.subscriptions?.length || 0} subscriptions</small></div>
              <span className={`trade-status ${user.active === false ? "inactive" : "active"}`}>{user.active === false ? "Inactive" : "Active"}</span>
              <button className="trade-link-button" onClick={() => edit(user)}>Edit</button>
              <button className="trade-link-button" onClick={() => resetPassword(user)}>Reset password</button>
            </article>
          ))}
        </div>
      </section>

      <section className="trade-card trade-editor">
        <span className="trade-eyebrow trade-green">{editor._id ? "EDIT SUBSCRIBER" : "NEW SUBSCRIBER"}</span>
        <h2>{editor._id ? editor.email : "Create access"}</h2>
        <form className="trade-form compact" onSubmit={save}>
          <label>Name<input value={editor.name} onChange={(event) => setEditor({ ...editor, name: event.target.value })} required /></label>
          <label>Email<input type="email" value={editor.email} onChange={(event) => setEditor({ ...editor, email: event.target.value })} required /></label>
          <label>Company<input value={editor.company} onChange={(event) => setEditor({ ...editor, company: event.target.value })} /></label>
          <label className="trade-check"><input type="checkbox" checked={editor.active} onChange={(event) => setEditor({ ...editor, active: event.target.checked })} /> Account active</label>
          <div className="trade-subscription-editor">
            <div className="trade-access-heading"><strong>Project and drawing access</strong><small>View access and email notifications can be configured separately.</small></div>
            {projects.map((project) => (
              <div className="trade-subscription-project" key={project._id}>
                <label className="trade-check project"><input type="checkbox" checked={editor.allowedProjects.includes(project._id)} onChange={() => toggleProject(project._id)} /> {project.name}</label>
                {editor.allowedProjects.includes(project._id) && (
                  <div className="trade-category-permissions">
                    <div className="trade-category-permission-header"><span>Drawing package</span><span>Can view</span><span>Email notifications</span></div>
                    {(categories[project._id] || []).map((category) => (
                      <div className="trade-category-permission-row" key={category._id}>
                        <span>{category.display_name || `${category.code} ${category.name}`}</span>
                        <label className="trade-check permission"><input type="checkbox" aria-label={`Allow ${category.display_name || category.name} access for ${project.name}`} checked={selectedSubscriptions.has(subscriptionKey(project._id, category._id))} onChange={() => toggleAccess(project._id, category._id)} /><span>View</span></label>
                        <label className="trade-check permission"><input type="checkbox" aria-label={`Email notifications for ${category.display_name || category.name} in ${project.name}`} checked={notificationSubscriptions.has(subscriptionKey(project._id, category._id))} disabled={!selectedSubscriptions.has(subscriptionKey(project._id, category._id))} onChange={() => toggleNotification(project._id, category._id)} /><span>Notify</span></label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          {message && <div className="trade-alert success">{message}</div>}
          <button className="trade-button primary" disabled={saving}>{saving ? "Saving…" : editor._id ? "Save subscriber" : "Create subscriber"}</button>
        </form>
      </section>
    </div>
  );
}
